import {
  type SelectionEffectFactory,
  type SelectionInputFactory,
} from '@databases/selections';
import { type DatabaseLayoutType } from '@databases/types/database/view';

/**
 * Well-known feature keys for the core selection features.
 * Using 'const' object + value extraction instead of enum
 * so the type remains open for extension.
 */
export const SelectionFeatureKeys = {
  // ── Inputs ──────────────────────────────
  CONTEXT_MENU_INPUT: 'context-menu-input',
  CHECKBOX_CELL_KEYBOARD_INPUT: 'checkbox-cell-keyboard-input',
  TABLE_CELL_ACTIONS_KEYBOARD_INPUT: 'table-cell-actions-keyboard-input',
  TABLE_TAB_NAVIGATION_KEYBOARD_INPUT: 'table-tab-navigation-keyboard-input',
  TABLE_NAVIGATION_KEYBOARD_INPUT: 'table-navigation-keyboard-input',
  MOUSE_INPUT: 'mouse-input',
  VIEW_CHANGES_INPUT: 'view-changes-input',
  YJS_CHANGES_INPUT: 'yjs-changes-input',

  // ── Effects ─────────────────────────────
  AIFC_DEBUG_SELECTIONS_EFFECT: 'aifc-debug-selections-effect',
  ACTION_BAR_EFFECT: 'action-bar-effect',
  CELL_EDITING_EFFECT: 'cell-editing-effect',
  CHECKBOX_EFFECT: 'checkbox-effect',
  CLEAR_CELLS_EFFECT: 'clear-cells-effect',
  CONTEXT_MENU_EFFECT: 'context-menu-effect',
  DELETE_INTENT_HIGHLIGHT_EFFECT: 'delete-intent-highlight-effect',
  FILL_EFFECT: 'fill-effect',
  FILL_PREVIEW_EFFECT: 'fill-preview-effect',
  FILL_HANDLE_EFFECT: 'fill-handle-effect',
  FOCUS_BOX_EFFECT: 'focus-box-effect',
  HIGHLIGHTS_EFFECT: 'highlights-effect',
  PRESENCE_EFFECT: 'presence-effect',
  PUBLISH_ROVO_EFFECT: 'publish-rovo-effect',
  SELECTIONS_PING_EFFECT: 'selections-ping-effect',
  TIMELINE_COLUMN_SELECTION_PING_EFFECT: 'timeline-column-selection-ping-effect',
  EXPERIMENT_EXPOSURE_EFFECT: 'experiment-exposure-event',
} as const;

/**
 * Open string literal union.
 *
 * Core keys get full IDE autocomplete. The `(string & {})` escape hatch
 * allows downstream code, A/B tests, or runtime plugins to inject custom
 * feature keys without modifying this file.
 *
 *  type SelectionFeatureKey = 'view-changes' | 'cell-editing' | ... | (string & {})
 */
export type SelectionFeatureKey =
  | (typeof SelectionFeatureKeys)[keyof typeof SelectionFeatureKeys]
  | (string & NonNullable<unknown>);

/**
 * Structural subset of DatabasePermissions from @databases/permissions.
 *
 * Duplicated here to avoid adding @databases/permissions as a dependency
 * of @databases/selections. The structural typing ensures compatibility
 * with the real DatabasePermissions at call sites.
 */
export interface SelectionPermissions {
  readonly canEditContent: boolean;
  readonly canEditFields: boolean;
  readonly canDelete: boolean;
  readonly isLocked: boolean;
  readonly isViewingVersion: boolean;
}

/**
 * Snapshot passed into each feature’s `isEnabled` when the selections machine
 * is assembled. Not reactive by itself: it only updates when the machine is
 * torn down and set up again (permissions change, or view id / layout type
 * change via `subscribeViewIdOrLayoutTypeForSelections` in app bootstrap).
 */
export interface SelectionFeatureActivationContext {
  /**
   * Mirrors `PermissionsManager.getPermissions()` at setup time.
   */
  readonly permissions: SelectionPermissions;
  /**
   * From `getCachedViewSnapshotForSelections().settings.layout.type` at setup time.
   *
   * This is undefined for extension layout IDs that do not map to a known
   * DatabaseLayoutType. Timeline is preserved even though it is not in the
   * built-in layout list.
   */
  readonly layoutType: DatabaseLayoutType | undefined;
}

/**
 * A selection feature is either an input or an effect that can be
 * conditionally included by a mode and self-filtered by activation context.
 */
export type SelectionFeature = SelectionInputFeature | SelectionEffectFeature;

interface SelectionFeatureBase {
  /** Stable identifier — used for debugging, logging, and deduplication. */
  key: SelectionFeatureKey;

  /**
   * Optional gate. If provided, the feature is EXCLUDED when this returns false.
   * If omitted, the feature is always active (assuming the mode included it).
   */
  isEnabled?: (ctx: SelectionFeatureActivationContext) => boolean;
}

export interface SelectionInputFeature extends SelectionFeatureBase {
  type: 'input';
  /**
   * Pre-bound factory — dependencies already captured via closure.
   * Called with the machine to produce the SelectionInput lifecycle.
   */
  factory: SelectionInputFactory;
}

export interface SelectionEffectFeature extends SelectionFeatureBase {
  type: 'effect';
  /**
   * Pre-bound factory — dependencies already captured via closure.
   * Returns a SelectionEffect when called.
   */
  factory: SelectionEffectFactory;
}
